import type { ColumnFrontType, ColumnType, perRowsType } from '@Types/Blocks/Columns';
import { type HeadingType } from '@Types/Shared/Heading';
import type { ColumnLink } from '@Types/Shared/RepeaterColumn';

import { Button } from '@Shared/Button/Button';
import Heading from '@Shared/Heading/Heading';

import type { ContentClass, TitleFormat } from '@/_types/Shared/Common';
import type { BlockKey } from '@/_types/Shared/Options';
import Html from '@/views/Shared/Html';

export default function ColumnClassic(props: ColumnFrontType) {
	const {
		id,
		titleFormat,
		title,
		perRow,
		columns,
		description,
		containerType,
		cardType,
		headingAlign,
		headingFontClassName,
		contentFontClass,
		context,
		sectionDecoration,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};

	let blockStyle = '';
	if (containerType === 'block') {
		blockStyle =
			'desktop:p-12 tablet:p-6 p-5 bg-minos-column-bg-container-color border-2 border-minos-column-container-border-color rounded-minos-column-container relative z-10';
	} else {
		blockStyle = '';
	}

	let cardStyle = '';
	if (cardType === 'block') {
		cardStyle =
			'desktop:p-6 tablet:p-4 p-3 bg-minos-column-bg-card-color border-2 rounded-minos-column-card border-minos-column-card-border-color';
	} else {
		cardStyle = '';
	}

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'pt-12 pb-16 tablet:pt-12 tablet:pb-24 desktop:pt-24 desktop:pb-40';
	} else {
		padding = 'py-5 tablet:py-12 desktop:py-24';
	}

	return (
		<div id={id} className={`minos-grid relative bg-minos-column-bg-color ${padding}`}>
			<div data-aos='fade-up' className={`relative z-[11] col-start-2 col-end-14 ${blockStyle}`}>
				{/* mobile breakpoints handle in component */}
				<Heading {...headingProps} />

				<div className='grid grid-cols-4 gap-y-5 tablet:grid-cols-12 tablet:gap-8'>
					{columns?.map((column: ColumnType) => {
						return (
							<Column
								key={column.id}
								column={column}
								perRow={perRow}
								cardType={cardStyle}
								contentFontClass={contentFontClass}
								context={context}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

const Column = ({
	column,
	perRow,
	cardType,
	contentFontClass,
	context,
}: {
	column: ColumnType;
	perRow: perRowsType;
	cardType: string;
	contentFontClass?: ContentClass;
	context?: BlockKey;
}) => {
	const { id, title, link, description, imageObj, titleFormat } = column;

	const props = {
		id: id,
		title: title,
		description: description,
		image: imageObj ? imageObj.url : '',
		imageAlt: imageObj ? imageObj.alt : '',
		link: link,
		titleFormat: titleFormat,
		context: context,
		cardType: cardType,
		contentFontClass: contentFontClass,
	};

	const views = {
		1: <ColumnPerRow className='col-span-full flex flex-col' {...props} />,
		2: <ColumnPerRow className='col-span-full flex flex-col tablet:col-span-6' {...props} />,
		3: <ColumnPerRow className='col-span-full flex flex-col tablet:col-span-4' {...props} />,
		4: <ColumnPerRow className='col-span-full flex flex-col tablet:col-span-3' {...props} />,
		6: <ColumnPerRow className='col-span-full flex flex-col tablet:col-span-2' {...props} />,
	};

	return <>{views[perRow]}</>;
};

type ColumnPerRowType = {
	id: string;
	imageAlt: string | undefined;
	image: string | undefined;
	title: string;
	description: string;
	link?: ColumnLink;
	className: string | '';
	cardType: string;
	contentFontClass?: ContentClass;
	context?: BlockKey;
	titleFormat: TitleFormat;
};

const ColumnPerRow = (props: ColumnPerRowType) => {
	const {
		id,
		image,
		imageAlt,
		title,
		description,
		link,
		className,
		cardType,
		contentFontClass,
		context,
		titleFormat,
	} = props;

	// Animation btn
	let animBtn;

	const animBtnPrimary = contentFontClass?.text?.btnPrimary?.animation;
	const animBtnSecondary = contentFontClass?.text?.btnSecondary?.animation;
	const animBtnTertiary = contentFontClass?.text?.btnTertiary?.animation;

	if (link?.style === 'primary') {
		animBtn = animBtnPrimary;
	} else if (link?.style === 'secondary') {
		animBtn = animBtnSecondary;
	} else if (link?.style === 'tertiary') {
		animBtn = animBtnTertiary;
	}

	return (
		<div data-aos='fade-up' id={id} className={`${className} ${cardType}`}>
			{image && (
				<img
					className='mb-4 aspect-[5/3] w-full rounded-minos-column-img object-cover tablet:mb-6'
					src={image}
					alt={imageAlt}
				/>
			)}

			{title && (
				<>
					{titleFormat === 'h2' ? (
						<h2 className={`mb-3 text-3xl tablet:mb-3.5 ${contentFontClass?.title}`}>{title}</h2>
					) : (
						<h3 className={`mb-3 text-2xl tablet:mb-3.5 ${contentFontClass?.title}`}>{title}</h3>
					)}
				</>
			)}

			{description && (
				<div data-content='minos-content' className='mb-3 last:mb-0 tablet:mb-3.5'>
					<Html context={context} className={contentFontClass?.text} html={description} />
				</div>
			)}

			{link?.url?.url && (
				<Button
					label={link.label}
					url={link.url.url}
					style={link.style}
					urlType={link.url.type}
					urlOpenNewTab={link.url.opensInNewTab}
					btnHasIcon={link.btnHasIcon}
					btnDecorationElement={link.btnDecorationElement}
					primaryClassname='minos-column-btn-primary tablet:minos-column-btn-primary'
					secondaryClassname='minos-column-btn-secondary tablet:minos-column-btn-secondary'
					tertiaryClassname='minos-column-btn-tertiary'
					animation={animBtn}
				/>
			)}
		</div>
	);
};
